import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import './Services.scss'
import FormikContact from '../Contact/FormikContact'
import { PageHero } from '../../components/ui/ui'
import useReveal from '../../hooks/useReveal'
import { processSteps, serviceCategories, services } from '../../data/services'
import { scrollToId } from '../../utils/scroll'

const PREVIEW_COUNT = 4

const ServiceCard = ({ service, index, highlighted, onDiscuss }) => {
  const [open, setOpen] = useState(highlighted)
  const extra = service.features.length - PREVIEW_COUNT
  const category = serviceCategories.find((item) => item.id === service.category)

  useEffect(() => { if (highlighted) setOpen(true) }, [highlighted])

  const trackSpotlight = (event) => {
    const rect = event.currentTarget.getBoundingClientRect()
    event.currentTarget.style.setProperty('--mx', `${event.clientX - rect.left}px`)
    event.currentTarget.style.setProperty('--my', `${event.clientY - rect.top}px`)
  }

  return (
    <article
      id={service.id}
      className={`svc-card ${open ? 'is-open' : ''} ${highlighted ? 'is-highlighted' : ''}`}
      style={{ '--i': index }}
      onMouseMove={trackSpotlight}
    >
      <div className="svc-card__top">
        <div className="svc-card__icon"><i className={service.icon} /></div>
        <span className="svc-card__cat"><i className={`fas ${category.icon}`} /> {category.label}</span>
      </div>
      <h3>{service.title}</h3>
      <p className="svc-card__desc">{service.description}</p>

      <ul className="svc-card__features">
        {service.features.slice(0, PREVIEW_COUNT).map((feature) => <li key={feature}>{feature}</li>)}
      </ul>
      {extra > 0 && (
        <div className="svc-card__more" aria-hidden={!open}>
          <ul className="svc-card__features">
            {service.features.slice(PREVIEW_COUNT).map((feature, i) => <li key={feature} style={{ '--d': `${i * 40}ms` }}>{feature}</li>)}
          </ul>
        </div>
      )}

      <div className="svc-card__tech">
        {service.technologies.map((tech) => <span key={tech}>{tech}</span>)}
      </div>

      <div className="svc-card__actions">
        {extra > 0 ? (
          <button type="button" className="svc-card__toggle" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
            {open ? 'Show less' : `+${extra} more`} <i className="fas fa-chevron-down" />
          </button>
        ) : <span />}
        <button type="button" className="svc-card__discuss" onClick={() => onDiscuss(service)}>
          Discuss this <i className="fas fa-arrow-right" />
        </button>
      </div>
    </article>
  )
}

const Services = () => {
  const rootRef = useRef(null)
  const searchRef = useRef(null)
  const { hash } = useLocation()
  const [params, setParams] = useSearchParams()
  const [query, setQuery] = useState(params.get('q') || '')
  const [topic, setTopic] = useState('')
  const category = params.get('category') || 'all'
  const highlightedId = hash ? hash.slice(1) : ''
  useReveal(rootRef)

  // Keep the filter + search in the URL so filtered views can be shared.
  const updateParams = (next) => {
    const merged = { category, q: query, ...next }
    const clean = {}
    if (merged.category && merged.category !== 'all') clean.category = merged.category
    if (merged.q) clean.q = merged.q
    setParams(clean, { replace: true })
  }

  const counts = useMemo(() => {
    const result = { all: services.length }
    services.forEach((service) => { result[service.category] = (result[service.category] || 0) + 1 })
    return result
  }, [])

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return services.filter((service) => {
      if (category !== 'all' && service.category !== category) return false
      if (!needle) return true
      return [service.title, service.description, ...service.features, ...service.technologies]
        .some((text) => text.toLowerCase().includes(needle))
    })
  }, [category, query])

  // Press "/" anywhere to jump into the search box.
  useEffect(() => {
    const onKey = (event) => {
      const typing = ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)
      if (event.key === '/' && !typing) {
        event.preventDefault()
        searchRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const discuss = (service) => {
    setTopic(service.title)
    scrollToId('contact')
  }

  return (
    <main className="ui-page svc-page" ref={rootRef}>
      <PageHero
        eyebrow="Services"
        title={<>Engineering services, from <span className="ui-gradient-text">idea to production</span>.</>}
        subtitle="Telecom platforms, AI automation, data warehousing and custom software, delivered end to end with the same care as my own products."
      >
        <div className="svc-hero__stats">
          <div><strong>{services.length}</strong><span>services</span></div>
          <div><strong>{serviceCategories.length - 1}</strong><span>disciplines</span></div>
          <div><strong>1</strong><span>point of contact</span></div>
        </div>
        <div className="svc-hero__actions">
          <a className="svc-btn svc-btn--primary" href="#catalogue" data-magnetic onClick={(event) => { event.preventDefault(); scrollToId('catalogue') }}>
            Browse services <i className="fas fa-arrow-down" />
          </a>
          <a className="svc-btn" href="#contact" data-magnetic onClick={(event) => { event.preventDefault(); scrollToId('contact') }}>
            <i className="fas fa-comments" /> Start a project
          </a>
        </div>
      </PageHero>

      <section id="catalogue" className="svc-catalogue">
        <div className="svc-toolbar">
          <div className="ui-container svc-toolbar__inner">
            <div className="svc-chips" role="tablist" aria-label="Filter services by category">
              {serviceCategories.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={category === item.id}
                  className={`svc-chip ${category === item.id ? 'is-active' : ''}`}
                  onClick={() => updateParams({ category: item.id })}
                >
                  <i className={`fas ${item.icon}`} /> {item.label}
                  <span className="svc-chip__count">{counts[item.id] || 0}</span>
                </button>
              ))}
            </div>
            <label className="svc-search">
              <i className="fas fa-magnifying-glass" />
              <input
                ref={searchRef}
                value={query}
                onChange={(event) => { setQuery(event.target.value); updateParams({ q: event.target.value }) }}
                placeholder="Search services, tech…"
                aria-label="Search services"
              />
              {query ? (
                <button type="button" onClick={() => { setQuery(''); updateParams({ q: '' }) }} aria-label="Clear search"><i className="fas fa-xmark" /></button>
              ) : <kbd>/</kbd>}
            </label>
          </div>
        </div>

        <div className="ui-container">
          <p className="svc-result" aria-live="polite">
            Showing <strong>{visible.length}</strong> of {services.length} services
            {category !== 'all' && <> in <strong>{serviceCategories.find((item) => item.id === category).label}</strong></>}
            {query && <> matching “<strong>{query}</strong>”</>}
          </p>

          {visible.length ? (
            // Keyed on the filter so the grid replays its entrance animation on every change.
            <div className="svc-grid" key={`${category}-${query}`}>
              {visible.map((service, index) => (
                <ServiceCard key={service.id} service={service} index={index} highlighted={highlightedId === service.id} onDiscuss={discuss} />
              ))}
            </div>
          ) : (
            <div className="svc-empty">
              <i className="fas fa-satellite-dish" />
              <h3>No services match that search</h3>
              <p>Try another keyword, or tell me what you need. If it involves software, the answer is probably yes.</p>
              <button type="button" className="svc-btn" onClick={() => { setQuery(''); updateParams({ q: '', category: 'all' }) }}>Reset filters</button>
            </div>
          )}
        </div>
      </section>

      <section className="svc-process">
        <div className="ui-container">
          <div className="svc-heading" data-reveal>
            <p className="ui-eyebrow">How we work</p>
            <h2>A delivery process with <span className="ui-gradient-text">no surprises</span>.</h2>
          </div>
          <ol className="svc-steps" data-reveal>
            {processSteps.map((step, index) => (
              <li key={step.title} style={{ '--i': index }}>
                <span className="svc-steps__dot"><i className={`fas ${step.icon}`} /></span>
                <span className="svc-steps__num">0{index + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <FormikContact topic={topic} />
    </main>
  )
}

export default Services
